import { FC, useCallback, useState } from 'react'
import { Box, Snackbar, Alert as MuiAlert } from '@mui/material'
import { addNewsArticleWithFiles, updateNewsArticleWithFiles, deleteNewsArticle, fetchNewsForAdminContent } from '@/features/news/newsThunks'
import NewsTable from './news/NewsTable'
import { useSnackbar } from '@/hooks/useSnackbar'
import { useFormManager } from '@/hooks/useFormManager'
import { parseDateToDayjs } from '@/utils/parseDateToDayjs'
import { NewsArticle } from '@/types'
import { Filehelper } from './Filehelper'
import NewsForm from './news/NewsForm'
import { NewsFormValues, INITIAL_NEWS_VALUES } from './news/newsForm.constants'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/app/store'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import LoadingOverlay from '@/components/common/LoadingOverlay'
import { toColombianDate } from '@/utils/colombianTimezone'

const transformNewsPayload = (values: NewsFormValues, isEditing: boolean) => {
  const hasNewImage = values.image instanceof File
  const hasNewDocument = values.document instanceof File

  const newsData = {
    title: values.title,
    tag: values.tag,
    site: values.site,
    date: toColombianDate(values.date),
    summary: values.description,
    content: values.body,
    imageName: Filehelper.getFileName(values.image),
    docName: Filehelper.getFileName(values.document),
    isNews: values.contentType === 'noticia',
  }

  if (isEditing) {
    return {
      id: values.id,
      newsData,
      image: hasNewImage ? values.image : undefined,
      document: hasNewDocument ? (values.document as File) : undefined,
    }
  }

  return {
    newsData,
    image: hasNewImage ? (values.image as File) : undefined,
    document: hasNewDocument ? (values.document as File) : undefined,
  }
}

const NewsManager: FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const snackbar = useSnackbar()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [articleToDelete, setArticleToDelete] = useState<NewsArticle | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const {
    values,
    editingId,
    isSubmitting,
    setFieldValue,
    handleEdit,
    handleReset,
    handleSubmit,
    SubmittingOverlay,
  } = useFormManager<NewsFormValues>({
    initialValues: INITIAL_NEWS_VALUES,
    onAddThunk: (payload) => addNewsArticleWithFiles(payload),
    onUpdateThunk: (id, payload) => updateNewsArticleWithFiles({ ...payload, id }),
    onFetchThunk: fetchNewsForAdminContent,
    transformPayload: transformNewsPayload,
  })

  const handleEditNews = useCallback(
    (article: NewsArticle) => {
      const mappedArticle: NewsFormValues = {
        id: article.id || '',
        contentType: article.isNews ? 'noticia' : 'comunicado',
        title: article.title || '',
        tag: article.tag || '',
        site: article.site || '',
        date: article.date ? parseDateToDayjs(article.date) : null,
        description: article.summary || '',
        body: article.content || '',
        image: article.imageName || null,
        document: article.docName || null,
      }
      handleEdit(mappedArticle, [])
      window.scrollTo({ top: 600, behavior: 'smooth' })
    },
    [handleEdit]
  )

  const handleDeleteNews = useCallback(
    (article: NewsArticle) => {
      if (!article.id) {
        snackbar.show('Error: ID de artículo no válido', 'error')
        return
      }
      setArticleToDelete(article)
      setDeleteDialogOpen(true)
    },
    [snackbar]
  )

  const confirmDelete = useCallback(async () => {
    if (!articleToDelete?.id) {
      setDeleteDialogOpen(false)
      return
    }

    setIsDeleting(true)
    try {
      await dispatch(deleteNewsArticle(articleToDelete.id)).unwrap()
      snackbar.show('Artículo eliminado correctamente', 'success')
      setDeleteDialogOpen(false)
      setArticleToDelete(null)
    } catch (error: any) {
      snackbar.show(error || 'Error al eliminar el artículo', 'error')
      setDeleteDialogOpen(false)
      setArticleToDelete(null)
    } finally {
      setIsDeleting(false)
    }
  }, [dispatch, snackbar, articleToDelete])

  const cancelDelete = useCallback(() => {
    setDeleteDialogOpen(false)
    setArticleToDelete(null)
  }, [])

  const validateFormData = useCallback(
    (): { valid: boolean; error?: string } => {
      if (values.date == null) {
        return { valid: false, error: 'Por favor selecciona una fecha' }
      }
      if (values.contentType === 'comunicado' && !values.document) {
        return { valid: false, error: 'Por favor sube un documento para el comunicado' }
      }
      return { valid: true }
    },
    [values.date, values.contentType, values.document]
  )

  const handleNewsSubmit = useCallback(
    async () => {
      const validation = validateFormData()
      if (!validation.valid) {
        snackbar.show(validation.error!, 'error')
        return
      }

      const contentTypeName = values.contentType === 'noticia' ? 'Noticia' : 'Comunicado'

      const onSubmit = handleSubmit(
        (isEditing: boolean) => {
          snackbar.show(
            isEditing ? `${contentTypeName} actualizada correctamente` : `${contentTypeName} agregado correctamente`,
            'success'
          )
        },
        (errorMsg: string) => snackbar.show(errorMsg, 'error')
      )
      await onSubmit()
    },
    [handleSubmit, snackbar, validateFormData, values.contentType]
  )

  return (
    <Box>
      {SubmittingOverlay()}
      {isDeleting && <LoadingOverlay message="Eliminando artículo, por favor espere..." />}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={snackbar.close}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert onClose={snackbar.close} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Confirmar eliminación"
        message="¿Estás seguro de que quieres eliminar el artículo"
        itemName={articleToDelete?.title}
        warningMessage="Esta acción no se puede deshacer. El artículo y sus archivos asociados serán eliminados permanentemente."
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmColor="error"
        isLoading={isDeleting}
      />

      <NewsForm
        values={values}
        editingId={editingId}
        isSubmitting={isSubmitting}
        setFieldValue={setFieldValue}
        onSubmit={handleNewsSubmit}
        onReset={handleReset}
      />

      <NewsTable onEdit={handleEditNews} onDelete={handleDeleteNews} />
    </Box>
  )
}

export default NewsManager